<template>
  <div
    class="oct-image-full"
    :data-ab-id="dataAbId"
  >
    <SharedLink :link="imgLink">
      <NuxtImg
        v-if="imageSrc"
        :src="imageSrc"
        sizes="lg:100vw 48vw"
        :srcset="imageSrcset"
        :alt="imgAlt"
        :title="imgTitle"
        :loading="imageLoadType"
        :preload="imageLoadType === 'eager'"
        :fetchpriority="imageLoadType === 'eager' ? 'high' : 'low'"
      />
    </SharedLink>
    <div class="oct-image-full__container">
      <span>{{ topTitle }}</span>
      <h2 v-if="title">
        {{ title }}
      </h2>
      <p>{{ subText }}</p>
      <SharedLink
        v-if="link && ctaText"
        :link="link"
      >
        {{ ctaText }}
      </SharedLink>
    </div>
  </div>
</template>

<script lang="ts" setup>
import magnoliaGetters from '~/composables/getters/magnoliaGetters';

interface Props {
  srcSet?: any[];
  image?: any;
  imageTitle?: string;
  imageLink?: any[];
  alt?: string;
  aspectRatio?: string;
  mobileAspectRatio?: string;
  maxImageHeight?: string;
  textPosition?: string;
  topTitle?: string;
  topTitleColor?: string;
  title?: string;
  titleColor?: string;
  subText?: string;
  subTextColor?: string;
  ctaLink?: any[];
  intLinkParam?: string;
  ctaTextColor?: string;
  buttonColor?: string;
  borderColor?: string;
  imageLoadType?: HTMLImageElement['loading'];
  dataAbId?: string;
}

const props = withDefaults(defineProps<Props>(), {
  srcSet: () => [],
  image: () => ({}),
  imageTitle: '',
  imageLink: () => [],
  alt: '',
  aspectRatio: 'original',
  mobileAspectRatio: '',
  maxImageHeight: '740',
  textPosition: '-50',
  topTitle: '',
  topTitleColor: '',
  title: '',
  titleColor: '',
  subText: '',
  subTextColor: '',
  ctaLink: () => [],
  ctaTextColor: '',
  buttonColor: '',
  borderColor: '',
  imageLoadType: 'lazy',
  intLinkParam: '',
});

const {
  image, imageLink, alt, aspectRatio, mobileAspectRatio, imageTitle, ctaLink, maxImageHeight, textPosition,
} = toRefs(props);
const { $domain } = useNuxtApp();
const link = computed(() => (ctaLink.value?.[0] ? magnoliaGetters.getLinkAndName(ctaLink.value[0], $domain.cmsAssetDomainPath, ctaLink.value[0].switchable?.intLinkParam) : {}));
const ctaText = computed(() => (ctaLink.value?.[0] ? ctaLink.value?.[0].ctaText : ''));
const imageSrc = computed(() => (image.value ? magnoliaGetters.getDefaultSrcAsset(image.value, $domain.cmsAssetDomainPath) : ''));
const imageSrcset = computed(() => (image.value ? magnoliaGetters.getSrcSetImagesAsString(image.value, $domain.cmsAssetDomainPath, aspectRatio.value, mobileAspectRatio.value === '' ? undefined : mobileAspectRatio.value) : []));
const imgLink = computed(() => (imageLink.value?.[0] ? magnoliaGetters.getLinkAndName(imageLink.value[0], $domain.cmsAssetDomainPath) : {}));
const imgAlt = computed<string>(() => (magnoliaGetters.getAltImage(image.value, alt.value)));
const imgTitle = computed<string>(() => (magnoliaGetters.getTitleImage(image.value, imageTitle.value)));
const maxImgHeight = computed(() => (`${maxImageHeight.value}px`));
const marginTopContainer = computed(() => (`${textPosition.value}px`));
</script>

<style lang="scss" scoped>
.oct-image-full {
  display: grid;
  margin: var(--spacer-base) 80px;

  //Tablet
  @include only-tablet {
    margin: var(--spacer-base) 50px;
  }

  //Mobile
  @include only-mobile {
    margin: var(--spacer-base) 20px;
  }

  img {
    width: 100%;
    max-height: v-bind(maxImgHeight);
    object-fit: cover;
  }

  &__container {
    z-index: 1;
    margin: 0 30px 30px;
    margin-top: v-bind(marginTopContainer);
    padding: 25px 80px;
    background: var(--c-white);
    box-shadow: 0 5px 20px 0 rgba(25, 25, 25, 0.08);
    text-align: center;

    //Tablet
    @include only-tablet {
      margin-top: 0;
      padding: 30px 54px;
    }

    //Mobile
    @include only-mobile {
      margin: 0;
      padding: 20px;
    }

    span {
      color: v-bind(topTitleColor);
      font-size: var(--font-size--sm);
      font-weight: bold;
      letter-spacing: 1px;
      text-transform: uppercase;

      //Mobile
      @include only-mobile {
        font-size: var(--font-size--xs);
        letter-spacing: 0.86px;
      }
    }

    h2 {
      color: v-bind(titleColor);
      font-size: 30px;
      font-weight: bold;
      letter-spacing: 0.22px;
      line-height: 41px;

      //Mobile
      @include only-mobile {
        font-size: 20px;
        letter-spacing: 0.15px;
        line-height: 27px;
      }
      //Tablet
      @include only-tablet {
        font-size: var(--h1-font-size);
        letter-spacing: 0;
        line-height: 33px;
      }
    }

    p {
      color: v-bind(subTextColor);
      margin: 10px 0 0 0;
      font-size: var(--font-size--base);
      letter-spacing: 0.5px;
      line-height: 22px;

      //Mobile
      @include only-mobile {
        margin: 5px 0 0 0;
        font-size: var(--font-size--sm);
        letter-spacing: 0.25px;
        line-height: 19px;
      }
    }

    :deep(a) {
      color: v-bind(ctaTextColor);
      background: v-bind(buttonColor);
      border: 1px solid v-bind(borderColor);
      display: inline-flex;
      margin-top: 25px;
      padding: 10px 30px;
      border-radius: 3px;
      box-shadow: 0 5px 20px 0 rgba(25, 25, 25, 0.08);
      font-size: var(--font-size--sm);
      font-weight: bold;
      letter-spacing: 1px;
      text-transform: uppercase;
      text-decoration: none;
      text-align: center;

      //Mobile
      @include only-mobile {
        margin-top: 15px;
        padding: 9px 30px;
      }
      //Tablet
      @include only-tablet {
        margin-top: 20px;
      }
    }
  }
}
</style>
